import { isEmpty } from 'lodash';
import {
    ERROR_TYPE,
    FriendsReferralApiService,
    ICampaignInfo,
    ICampaignStatus,
    IGetInviteInfo
} from '@sitegen/common/classes/friends-referral-api';
import { UbisoftAPIService } from 'modules/ubisoft-api';
import { State } from 'widgets/friends-referral/root/root.reducers';

import {
    FRIENDS_REFERRAL_FAIL_CAMPAIGN_STATUS,
    FRIENDS_REFERRAL_GOT_INVITATION_INFO,
    FRIENDS_REFERRAL_GOT_STATUS_INFO,
    FRIENDS_REFERRAL_INVITATION_ACCEPTED,
    FRIENDS_REFERRAL_NOT_LINKED,
    FRIENDS_REFERRAL_SET_CAMPAIGN_INFO,
    FRIENDS_REFERRAL_SET_ERROR,
    FRIENDS_REFERRAL_SET_INELIGIBLE_ERROR,
    FRIENDS_REFERRAL_SET_INVITATION_INFO,
    FRIENDS_REFERRAL_SET_LOADING,
    FRIENDS_REFERRAL_SET_PLATFORMS_OWNERSHIP,
    FRIENDS_REFERRAL_SET_PLAYER_INFO,
    FRIENDS_REFERRAL_SET_PLAYER_STATS,
    FRIENDS_REFERRAL_SET_STAT_SERVICE_ERROR,
    FRIENDS_REFERRAL_SHOW_REWARDS_IMAGE_LIGHTBOX,
    FRIENDS_REFERRAL_SUCCESS_CAMPAIGN_STATUS,
    FRIENDS_REFERRAL_MAINTENANCE_CAMPAIGN_STATUS,
    FRIENDS_REFERRAL_SET_REGISTERED_REFERRER_INFO
} from './friends-referral-store.constants';

import { IFriendReferralsInfo, IPlatformsOwnership, IPlayerStats } from './friends-referral-store.types';
import {
    IGetPlayerInfo
} from '@sitegen/common/classes/friends-referral-api/friends-referral-api.types';

import { Dispatch } from 'types/dispatch';
import { IProfilePlatform } from 'types/profiles';

import { PLATFORM_TYPES } from '@sitegen/common/constants/platforms';

export const setRegisteredReferrerInfo = (platform: PLATFORM_TYPES, invitationId: string) => ({
    type: FRIENDS_REFERRAL_SET_REGISTERED_REFERRER_INFO,
    platform,
    invitationId
});
export const setPlatformsOwnership = (platformsOwnership: IPlatformsOwnership) => ({
    type: FRIENDS_REFERRAL_SET_PLATFORMS_OWNERSHIP,
    platformsOwnership
});
export const setPlayerReferralInfo = (playerInfo: IGetPlayerInfo) =>
    ({ type: FRIENDS_REFERRAL_SET_PLAYER_INFO, playerInfo });
export const gotFriendsReferralInfo = (friendsReferralInfo: IFriendReferralsInfo) =>
    ({ type: FRIENDS_REFERRAL_GOT_STATUS_INFO, payload: friendsReferralInfo });

export const setLoading = (loading: boolean) => ({ type: FRIENDS_REFERRAL_SET_LOADING, loading });

export const setError = (error: Error) => ({ type: FRIENDS_REFERRAL_SET_ERROR, error });

export const setIneligibleError = () => ({ type: FRIENDS_REFERRAL_SET_INELIGIBLE_ERROR });

export const setFailStatsError = () => ({ type: FRIENDS_REFERRAL_SET_STAT_SERVICE_ERROR });

export const setSuccessCampaignStatus = () => ({
    type: FRIENDS_REFERRAL_SUCCESS_CAMPAIGN_STATUS
});

export const setMaintenanceCampaignStatus = () => ({
    type: FRIENDS_REFERRAL_MAINTENANCE_CAMPAIGN_STATUS
});

export const setFailCampaignStatus = () => ({
    type: FRIENDS_REFERRAL_FAIL_CAMPAIGN_STATUS
});

export const showRewardsImageLightbox = (opened: boolean) => ({
    type: FRIENDS_REFERRAL_SHOW_REWARDS_IMAGE_LIGHTBOX,
    payload: opened
});

export const getCampaignStatus = () => {
    return async (dispatch: Dispatch): Promise<void> => {
        dispatch(setLoading(true));

        try {
            const status: ICampaignStatus = await FriendsReferralApiService.getCampaignStatus();
            if (status.success && status.isSuccessful) {
                dispatch(setSuccessCampaignStatus());
            } else {
                dispatch(setMaintenanceCampaignStatus());
            }
        } catch (e) {
            dispatch(setFailCampaignStatus());
        }
    };
};

export const registerUser = (platform: PLATFORM_TYPES) => {
    return async (dispatch: Dispatch): Promise<void> => {
        dispatch(setLoading(true));

        try {
            const registeredReferrer = await FriendsReferralApiService.register(platform);
            dispatch(setRegisteredReferrerInfo(platform, registeredReferrer.invitationId));
        } catch (e) {
            dispatch(setError(e));
        }
    };
};

export const acceptInvitation = () => {
    return async (dispatch: Dispatch, getState: () => State): Promise<void> => {
        dispatch(setLoading(true));
        const invitationId: string = getState().friendsReferral.invitationInfo!.invitationId!;
        try {
            await FriendsReferralApiService.acceptInvitation({ invitationId });
            await dispatch(getInviteeProgressStat());
        } catch (error) {
            const errorOptions = {
                [ERROR_TYPE.API_ERROR]: () => setIneligibleError(),
                default: () => setError(error)
            };

            const errorType = errorOptions[error.type] && errorOptions[error.type]()
                || errorOptions.default();
            return dispatch(errorType);
        }
    };
};

export const getInvitationInfo = (invitationId: string) => {
    return async (dispatch: Dispatch): Promise<void> => {
        dispatch(setLoading(true));
        try {
            const invitationInfo: IGetInviteInfo = await FriendsReferralApiService.getInviteInfo({ invitationId });
            dispatch(setInvitationInfo({ ...invitationInfo, invitationId }));
            dispatch(gotInvitationInfo());
        } catch (error) {
            const errorOptions = {
                [ERROR_TYPE.API_ERROR]: () => setIneligibleError(),
                default: () => setError(error)
            };

            const errorType = errorOptions[error.type] && errorOptions[error.type]()
                || errorOptions.default();
            return dispatch(errorType);
        }
    };
};

export const updateInvitationInfo = (options: { readonly referrerUbiAccountId: string, readonly platformId: PLATFORM_TYPES }) => {
    return async (dispatch: Dispatch): Promise<void> => {
        dispatch(setLoading(true));
        try {
            const referrerInfo: IProfilePlatform = await UbisoftAPIService.getFriendProfile({ UbiAccountId: options.referrerUbiAccountId });
            dispatch(setInvitationInfo({
                username: referrerInfo.nameOnPlatform!,
                platformId: options.platformId,
                invitationId: undefined
            }));
            await dispatch(getInviteeProgressStat());
        } catch (e) {
            dispatch(setError(e));
        }
    };
};

export const getInviteeProgressStat = () => {
    return async (dispatch: Dispatch, getState: () => State): Promise<void> => {
        try {
            let userId = '';
            const state = getState();

            const platformId: PLATFORM_TYPES = state.friendsReferral.invitationInfo!.platformId;
            const user = state.login.loginSession?.UbisoftConnectProfile?.user;
            const profiles: [IProfilePlatform] = state.login.loginSession?.UbisoftConnectProfiles ?? [];
            const linkedProfile: IProfilePlatform | undefined = profiles
                .find(profile => (profile.id === platformId && !isEmpty(profile.profileId)));

            const isCrossPlatform = state.friendsReferral.campaignInfo?.crossPlatform;
            const isLinkedProfile = isCrossPlatform
                ? true
                : Boolean(linkedProfile);

            if (!isLinkedProfile) {
                dispatch(setAccountNotLinked());
                return;
            }

            if (isCrossPlatform) {
                userId = linkedProfile?.userId ? linkedProfile.userId : user!.userId;
            } else {
                userId = linkedProfile!.profileId!;
            }

            const statParamName: string = state.friendsReferral.campaignInfo!.conversion.paramName;
            const campaignSpaces = state.friendsReferral.campaignInfo!.spaces;
            const statName: string = statParamName.split('.')[1];
            const spaceId: string = campaignSpaces[platformId];

            const playerStat: { [name: string]: string } = await UbisoftAPIService.getStatByName({
                userId,
                spaceId,
                statName
            });

            dispatch(setPlayerStat({ [statParamName]: playerStat[statName] }));
            dispatch(gotInvitationAccepted());
        } catch {
            dispatch(setFailStatsError());
        }
    };
};

export const getFriendsReferralInfo = () => {
    return async (dispatch: Dispatch): Promise<void> => {
        dispatch(setLoading(true));

        try {
            const [platformsOwnership, playerInfoResponse, campaignInfoResponse] = await Promise.all([
                FriendsReferralApiService.getOwnershipInfo(),
                FriendsReferralApiService.getPlayerInfo(),
                FriendsReferralApiService.getCampaignInfo()
            ]);

            dispatch(setPlatformsOwnership(platformsOwnership));
            dispatch(setPlayerReferralInfo(playerInfoResponse));
            dispatch(setCampaignInfo(campaignInfoResponse));
        } catch (e) {
            dispatch(setError(e));
        }
    };
};

export const setCampaignInfo = (campaignInfo: ICampaignInfo) => ({
    type: FRIENDS_REFERRAL_SET_CAMPAIGN_INFO,
    campaignInfo
});
export const setPlayerStat = (playerStats: IPlayerStats) => ({ type: FRIENDS_REFERRAL_SET_PLAYER_STATS, playerStats });

export const setInvitationInfo = (invitationInfo: IGetInviteInfo) => ({
    type: FRIENDS_REFERRAL_SET_INVITATION_INFO,
    invitationInfo
});
export const gotInvitationAccepted = () => ({ type: FRIENDS_REFERRAL_INVITATION_ACCEPTED });
export const gotInvitationInfo = () => ({ type: FRIENDS_REFERRAL_GOT_INVITATION_INFO });
export const setAccountNotLinked = () => ({ type: FRIENDS_REFERRAL_NOT_LINKED });

export type Actions = ReturnType<typeof setLoading>
| ReturnType<typeof setError>
| ReturnType<typeof setPlatformsOwnership>
| ReturnType<typeof setPlayerReferralInfo>
| ReturnType<typeof setInvitationInfo>
| ReturnType<typeof setCampaignInfo>
| ReturnType<typeof setPlayerStat>
| ReturnType<typeof setRegisteredReferrerInfo>
| ReturnType<typeof showRewardsImageLightbox>
| ReturnType<typeof setSuccessCampaignStatus>
| ReturnType<typeof setMaintenanceCampaignStatus>
| ReturnType<typeof setFailCampaignStatus>;
